import React, { useEffect, useRef } from "react";

/**
 * Background neural lines for the dashboard. Subtler than the landing canvas:
 * fewer nodes, no signal storms, content remains readable.
 */
const NeuralLines = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let nodes = [];
    let raf = 0;
    let w = 0, h = 0;

    const palette = () => {
      const css = (n) => getComputedStyle(document.documentElement).getPropertyValue(n).trim().split(/\s+|,/).map((x) => parseInt(x, 10));
      return [css("--accent-cyan-rgb"), css("--accent-blue-rgb"), css("--accent-violet-rgb")];
    };
    let cols = palette();

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const isMobile = w < 768;
      const count = isMobile ? 26 : 50;
      nodes = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.05, vy: (Math.random() - 0.5) * 0.05,
        r: 1 + Math.random() * 1.4,
        col: cols[i % 3],
        pulse: Math.random() * Math.PI * 2,
      }));
    };

    const step = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.pulse += 0.012;
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      // edges
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d > 160) continue;
          const alpha = (1 - d / 160) * 0.08;
          ctx.strokeStyle = `rgba(${a.col[0]}, ${a.col[1]}, ${a.col[2]}, ${alpha})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        }
      }
      // nodes
      for (const n of nodes) {
        const energy = 0.4 + Math.sin(n.pulse) * 0.2;
        ctx.fillStyle = `rgba(${n.col[0]}, ${n.col[1]}, ${n.col[2]}, ${0.55 * energy})`;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill();
      }
      raf = requestAnimationFrame(step);
    };

    const onResize = () => { dpr = Math.min(window.devicePixelRatio || 1, 2); build(); };
    const onTheme = () => { cols = palette(); };
    build(); raf = requestAnimationFrame(step);
    window.addEventListener("resize", onResize);
    window.addEventListener("aura:theme", onTheme);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("aura:theme", onTheme);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      data-testid="dashboard-neural-lines"
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 0, pointerEvents: "none", opacity: 0.55 }}
    />
  );
};

export default NeuralLines;
